import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigationDelayRef = useRef<NodeJS.Timeout | null>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const mobileServicesRef = useRef<HTMLDivElement>(null);
  const focusableElementsRef = useRef<HTMLElement[]>([]);
  const navigate = useNavigate();

  const services = [
    { name: 'الإنشاءات والطرق', path: '/services/construction' },
    { name: 'البترول', path: '/services/oil' },
    { name: 'الذهب', path: '/services/gold' },
    { name: 'التسليح', path: '/services/defense' },
    { name: 'الاستشارات', path: '/services/consulting' }
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Desktop hover handlers
  const handleMouseEnter = () => {
    if (isMobile) return;
    
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(true);
    }, 100);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    closeTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 350);
  };

  // Mobile/Desktop click handler for services toggle
  const handleServicesToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsServicesOpen(!isServicesOpen);
  };

  // Handle service link navigation with delay for mobile
  const handleServiceNavigation = (path: string, e: React.MouseEvent) => {
    if (isMobile) {
      // Allow immediate navigation
      navigate(path);
      
      // Close menu after navigation delay
      if (navigationDelayRef.current) {
        clearTimeout(navigationDelayRef.current);
      }
      
      navigationDelayRef.current = setTimeout(() => {
        setIsServicesOpen(false);
        setIsMobileMenuOpen(false);
      }, 350);
    } else {
      // Desktop behavior - close immediately
      setIsServicesOpen(false);
      navigate(path);
    }
  };

  // Close on outside click for mobile
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (isMobile && isServicesOpen) {
        // Mobile services dropdown
        if (
          mobileServicesRef.current &&
          !mobileServicesRef.current.contains(target)
        ) {
          setIsServicesOpen(false);
        }
      } else if (!isMobile && isServicesOpen) {
        // Desktop services dropdown
        if (
          servicesRef.current &&
          panelRef.current &&
          !servicesRef.current.contains(target) &&
          !panelRef.current.contains(target)
        ) {
          setIsServicesOpen(false);
        }
      }
    };

    if (isServicesOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isServicesOpen, isMobile]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isServicesOpen && isMobile) {
      const focusableElements = mobileServicesRef.current?.querySelectorAll(
        'a[href], button:not([disabled])'
      ) as NodeListOf<HTMLElement>;
      
      if (focusableElements) {
        focusableElementsRef.current = Array.from(focusableElements);
        focusableElements[0]?.focus();
      }
    }
  }, [isServicesOpen, isMobile]);

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsServicesOpen(false);
      return;
    }

    if (event.key === 'Enter' && event.currentTarget.getAttribute('role') === 'button') {
      event.preventDefault();
      handleServicesToggle(event as any);
      return;
    }

    // Arrow key navigation for mobile
    if (isMobile && isServicesOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const focusableElements = focusableElementsRef.current;
      const currentIndex = focusableElements.findIndex(el => el === document.activeElement);
      
      if (event.key === 'ArrowDown') {
        const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        focusableElements[nextIndex]?.focus();
      } else {
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        focusableElements[prevIndex]?.focus();
      }
    }
  };

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (navigationDelayRef.current) {
        clearTimeout(navigationDelayRef.current);
      }
    };
  }, []);

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-[1300] overflow-visible h-[72px]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/b722d8dcf1c144135667d61789b02d4e/99b5c3dffef714fa12a155204a44cba1.png" 
                alt="شركة فيبا التجارية" 
                className="h-12 w-auto"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8 space-x-reverse">
              <Link to="/" className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium">
                الرئيسية
              </Link>
              
              {/* Services Dropdown */}
              <div 
                ref={servicesRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onKeyDown={handleKeyDown}
              >
                <button 
                  className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium flex items-center cursor-pointer whitespace-nowrap focus:outline-none focus:text-[#E71B25]"
                  onClick={handleServicesToggle}
                  onFocus={() => !isMobile && setIsServicesOpen(true)}
                  aria-expanded={isServicesOpen}
                  aria-haspopup="true"
                >
                  الخدمات
                  <i className={`ri-arrow-down-s-line mr-1 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {/* Desktop Dropdown Panel */}
                <div
                  ref={panelRef}
                  className={`absolute top-full right-0 mt-0 min-w-[260px] bg-white rounded-lg shadow-lg border border-[#C3AAAB33] z-[1000] transition-all duration-200 ${
                    isServicesOpen 
                      ? 'opacity-100 visible translate-y-0' 
                      : 'opacity-0 invisible -translate-y-2'
                  }`}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Hover Bridge */}
                  <div className="absolute top-[-10px] left-0 right-0 h-[10px]"></div>
                  
                  <div className="py-2">
                    {services.map((service, index) => (
                      <a
                        key={index}
                        href={service.path}
                        className="block px-4 py-3 text-[#595A5C] hover:bg-[#F7F7F7] hover:text-[#E71B25] transition-colors text-right cursor-pointer focus:bg-[#F7F7F7] focus:text-[#E71B25] focus:outline-none"
                        onClick={(e) => {
                          e.preventDefault();
                          handleServiceNavigation(service.path, e);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleServiceNavigation(service.path, e as any);
                          }
                        }}
                        tabIndex={isServicesOpen ? 0 : -1}
                      >
                        {service.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              
              <Link to="/about" className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium">
                من نحن
              </Link>
              
              <Link to="/contact" className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium">
                تواصل معنا
              </Link>
            </nav>
            
            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4 space-x-reverse">
              <a 
                data-lang="en"
                className="lang-btn text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium cursor-pointer"
                aria-label="Switch to English"
              >
                English
              </a>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = 'mailto:info@vipatradingco.com'}
              >
                مراسلة عبر البريد
              </Button>
              <Button variant="primary" size="sm">
                طلب عرض سعر
              </Button>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden text-[#595A5C] cursor-pointer"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="القائمة"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t z-[1200]">
              <nav className="p-4 space-y-4">
                <Link 
                  to="/" 
                  className="block text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  الرئيسية
                </Link>
                
                {/* Mobile Services Accordion */}
                <div ref={mobileServicesRef}>
                  <button
                    onClick={handleServicesToggle}
                    onKeyDown={handleKeyDown}
                    className="w-full flex items-center justify-between text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium cursor-pointer whitespace-nowrap focus:outline-none focus:text-[#E71B25] min-h-[44px] py-2"
                    aria-expanded={isServicesOpen}
                    aria-haspopup="true"
                    role="button"
                  >
                    الخدمات
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  
                  {isServicesOpen && (
                    <div className="mt-3 pr-4 space-y-1 border-r-2 border-[#E71B25]/20">
                      {services.map((service, index) => (
                        <a
                          key={index}
                          href={service.path}
                          className="block text-[#595A5C] hover:text-[#E71B25] hover:bg-[#F7F7F7] transition-colors text-sm cursor-pointer rounded px-3 py-3 min-h-[44px] flex items-center focus:outline-none focus:bg-[#F7F7F7] focus:text-[#E71B25]"
                          onClick={(e) => {
                            e.preventDefault();
                            handleServiceNavigation(service.path, e);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleServiceNavigation(service.path, e as any);
                            }
                          }}
                          tabIndex={0}
                        >
                          {service.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                
                <Link 
                  to="/about" 
                  className="block text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  من نحن
                </Link>
                
                <Link 
                  to="/contact" 
                  className="block text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  تواصل معنا
                </Link>

                {/* Mobile CTA Buttons */}
                <div className="pt-4 border-t space-y-3">
                  <a 
                    data-lang="en"
                    className="lang-btn-mobile block text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium text-center py-2 cursor-pointer"
                    aria-label="Switch to English"
                  >
                    English
                  </a>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => window.location.href = 'mailto:info@vipatradingco.com'}
                  >
                    مراسلة عبر البريد
                  </Button>
                  <Button variant="primary" size="sm" className="w-full">
                    طلب عرض سعر
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Services Backdrop - Only for mobile services dropdown */}
      {isMobile && isServicesOpen && (
        <div 
          className="fixed inset-0 z-[1100] pointer-events-auto"
          onClick={() => setIsServicesOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}